/* eslint-disable functional/immutable-data */

import type { Cache } from '@urql/exchange-graphcache';

import type {
  BillingAccount,
  Customer,
  DeleteBillingAccountInput,
  DeleteBillingAccountMutation,
  SubmitBraintreeBillingAccountInput,
  SubmitBraintreeBillingAccountMutation,
  SubmitBraintreeBillingAccountSuccessResponse,
} from '@order/graphql';
import { CurrentCustomerDocument } from '@order/graphql';

/**
 * Changing billing accounts returns a billing account object.
 * This is fine for when the billing account is updated,
 * But if it is added or removed, this is needed to update
 * the current customer's object's billing accounts in URQL cache.
 */

export const submitBraintreeBillingAccount = (
  result: SubmitBraintreeBillingAccountMutation,
  _args: SubmitBraintreeBillingAccountInput,
  cache: Cache,
) => {
  const response = result.submitBraintreeBillingAccount;
  const data = response as SubmitBraintreeBillingAccountSuccessResponse;
  const typename = response.__typename;

  // Ignore unsuccessful mutations.
  if (typename !== 'SubmitBraintreeBillingAccountSuccessResponse') return;

  updateCacheWithBillingAccountChanges(cache, data);
};

export const deleteBillingAccount = (
  result: DeleteBillingAccountMutation,
  args: { readonly input: DeleteBillingAccountInput },
  cache: Cache,
) => {
  const deletedId = args?.input.id;

  // Ignore unsuccessful mutations.
  if (!deletedId || !result?.deleteBillingAccount?.success) return;

  updateCacheWithBillingAccountChanges(cache, {
    billingAccount: { id: String(deletedId) },
    removed: true,
  });
};

const updateCacheWithBillingAccountChanges = (
  customerCache: Cache,
  billingAccountChanges: BillingAccountChanges,
) => {
  customerCache.updateQuery({ query: CurrentCustomerDocument }, (data) => {
    if (!data) return data;

    // Get cached billing accounts.
    const cachedCustomer = data?.currentCustomer as Customer;
    const billingAccounts = cachedCustomer?.billingAccounts ?? [];
    const withoutUpdated = billingAccounts.filter(
      ({ id }) => id !== billingAccountChanges.billingAccount.id,
    );

    // If the billing account was removed, remove it from the cache.
    if (billingAccountChanges.removed) {
      data.currentCustomer = {
        ...cachedCustomer,
        billingAccounts: withoutUpdated,
      };

      return data;
    }

    // If the credit card was added, add it and sort by id.
    data.currentCustomer = {
      ...cachedCustomer,
      billingAccounts: [
        ...withoutUpdated,
        billingAccountChanges.billingAccount as BillingAccount,
      ].sort((c1, c2) => c1.id.localeCompare(c2.id)),
    };

    return data;
  });
};

type BillingAccountChanges = {
  readonly billingAccount: Partial<BillingAccount>;
  readonly removed?: boolean;
};
